/* Complimentary Tank Water Health Check — campaign constants + UTM capture.
   Sep 2026 paid-social funnel (/tank-water-health-check). */

export const THC = {
  navy: "#073A7A",
  blue: "#0877CC",
  bright: "#09A9E6",
  light: "#EAF7FD",
  faint: "#F4FAFD",
  body: "#173452",
  muted: "#547088",
  red: "#F22B22",
  /* Jun 2026 — owner-attendance messaging moved from red (reads as an
     error / rejection) to yellow (a heads-up). `yellowInk` is the
     accessible text/icon tone for use on white or light yellow. */
  yellow: "#F5B700",
  yellowSoft: "#FFF8E6",
  yellowInk: "#8A5A00",
  /* Campaign bonus card — green (a reward, not a warning). */
  green: "#0F8A4C",
  border: "#D6E8F2",
  shadow: "0 4px 18px rgba(7, 49, 100, 0.08)",
};

export const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/9c513750-f322-403f-a65c-e0145b871f05/images/43f8cb58d9dc8b266322c54a969700151ffb4c1e4602e2d87f490f36f2757778.jpeg";
export const FOOTER_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/9c513750-f322-403f-a65c-e0145b871f05/images/ed4bb2410937c4679c983f5c29944446680824309f57a362d91d9a2dc7e30e94.jpeg";

export const WATER_SOURCE_LABELS = {
  rainwater: "Rainwater", bore: "Bore water", both: "Rainwater + bore water",
};

export const QUESTIONS = [
  { field: "water_source", label: "Does this property rely on bore and/or rainwater tank water?",
    options: [["rainwater", "Rainwater"], ["bore", "Bore water"], ["both", "Both"], ["none", "None"]] },
  { field: "drink_or_cook", label: "Do you drink or cook with this water?",
    options: [["yes_regularly", "Yes, regularly"], ["sometimes", "Sometimes"], ["no", "No"]] },
  { field: "filtration_status", label: "Do you currently have water filtration?",
    options: [["yes", "Yes"], ["no", "No"], ["unsure", "Unsure"]] },
  { field: "uv_status", label: "Do you have UV disinfection?",
    options: [["yes", "Yes"], ["no", "No"], ["unsure", "Unsure"]] },
  { field: "last_tank_clean", label: "When was your tank last professionally cleaned?",
    options: [["within_2_years", "Within 2 years"], ["2_5_years", "2–5 years ago"],
      ["more_than_5_years", "More than 5 years ago"], ["never", "Never"], ["not_sure", "Not sure"]] },
  { field: "previous_lab_test", label: "Have you ever had your water laboratory tested?",
    options: [["yes", "Yes"], ["no", "No"], ["not_sure", "Not sure"]] },
  { field: "owner_present", label: "Will the property owner be present for the Health Check?",
    options: [["yes", "Yes"], ["no", "No"]] },
];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const ATTR_KEYS = [...UTM_KEYS, "fbclid"];
const STORE_KEY = "thc_attribution";
const FIRST_KEY = "thc_first_touch";
const SESSION_KEY = "thc_anon_session";
const BEACON_KEY = "thc_visit_sent";

/** Anonymous browser id — no PII, never tied to a customer record. */
export function anonymousSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `thc_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `thc_${Date.now().toString(36)}`;
  }
}

/** Capture UTMs + fbclid on first landing and keep them for the whole funnel,
    including the confirmation route. First-touch is written once. */
export function captureAttribution(search) {
  const params = new URLSearchParams(search || "");
  const stored = readAttribution();
  const next = { ...stored };
  let changed = false;
  ATTR_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) { next[k] = v; changed = true; }
  });
  if (changed) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    try {
      if (!localStorage.getItem(FIRST_KEY)) {
        localStorage.setItem(FIRST_KEY, JSON.stringify(next));
      }
    } catch { /* private mode */ }
  }
  return next;
}

/** The immutable first-touch this browser saw, if any. */
export function readFirstTouch() {
  try {
    const raw = localStorage.getItem(FIRST_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

/** Anonymous funnel event (health_check_start). Fired at most once per
    session on the customer's first real interaction with the booking flow. */
export function sendStartEvent(search, trigger) {
  const START_KEY = "thc_start_sent";
  try {
    if (localStorage.getItem(START_KEY)) return;
    localStorage.setItem(START_KEY, new Date().toISOString());
  } catch { /* private mode — the server de-duplicates anyway */ }

  const params = new URLSearchParams(search || "");
  const queryParams = {};
  params.forEach((v, k) => { queryParams[k] = v; });
  const body = {
    event_type: "health_check_start",
    anonymous_session_id: anonymousSessionId(),
    landing_path: "/tank-water-health-check",
    trigger: trigger || "booking_flow_interaction",
    device_type: window.innerWidth < 768 ? "mobile" : "desktop",
    query_params: queryParams,
    ...ATTR_KEYS.reduce((acc, k) => {
      const v = params.get(k);
      if (v) acc[k] = v;
      return acc;
    }, {}),
  };
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaign/event`;
  const payload = JSON.stringify(body);
  try {
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon?.(url, blob)) return;
  } catch { /* fall through */ }
  fetch(url, { method: "POST", keepalive: true,
    headers: { "Content-Type": "application/json" }, body: payload }).catch(() => {});
}

/** Everything the booking needs to keep attribution intact server-side. */
export function attributionPayload() {
  const stored = readAttribution();
  const first = readFirstTouch();
  return {
    ...stored,
    anonymous_session_id: anonymousSessionId(),
    ...(first ? { first_touch: first } : {}),
  };
}

/** Anonymous server-side landing visit. Non-blocking, once per session/day,
    de-duplicated and filtered again on the server. No PII in the payload. */
export function sendVisitBeacon(search, pathname) {
  const path = pathname || "/tank-water-health-check";
  if (!path.startsWith("/tank-water-health-check")) return;
  const day = new Date().toISOString().slice(0, 10);
  const stamp = `${path}|${day}`;
  try {
    if (sessionStorage.getItem(BEACON_KEY) === stamp) return;
    sessionStorage.setItem(BEACON_KEY, stamp);
  } catch { /* private mode — the server de-duplicates anyway */ }

  const params = new URLSearchParams(search || "");
  const queryParams = {};
  params.forEach((v, k) => { queryParams[k] = v; });
  const body = {
    anonymous_session_id: anonymousSessionId(),
    landing_path: path,
    referrer: document.referrer || undefined,
    device_type: window.innerWidth < 768 ? "mobile" : "desktop",
    query_params: queryParams,
    ...ATTR_KEYS.reduce((acc, k) => {
      const v = params.get(k);
      if (v) acc[k] = v;
      return acc;
    }, {}),
  };
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaign/visit`;
  const payload = JSON.stringify(body);
  try {
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon?.(url, blob)) return;
  } catch { /* fall through */ }
  fetch(url, { method: "POST", keepalive: true,
    headers: { "Content-Type": "application/json" }, body: payload }).catch(() => {});
}

export function readAttribution() {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(STORE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
  }
  return {};
}

/** Property details merged from a postcard/campaign link. */
export function addressFromQuery(search) {
  const p = new URLSearchParams(search || "");
  const street = p.get("street_address") || p.get("street") || "";
  const suburb = p.get("suburb") || "";
  const state = (p.get("state") || "").toUpperCase();
  const postcode = p.get("postcode") || "";
  const full = p.get("address") || p.get("property_address") ||
    [street, [suburb, state, postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return {
    street_address: street, suburb, state, postcode, property_address: full,
    // Merged from our own postcard/campaign data — treat as confirmed but
    // still fully editable (re-selecting re-verifies through Google).
    address_verified: Boolean(full),
    address_source: full ? "campaign_merge" : "typed",
    country: "AU",
  };
}

/** The master address, ready for the booking record. A Google selection
    already carries every field; a campaign-merged value only has the text,
    so fill the gaps by parsing it. */
export function resolveAddress(address) {
  const full = (address.property_address || "").trim();
  const [first, ...rest] = full.split(",");
  const tail = rest.join(",").trim();
  const m = tail.match(/^(.*?)\s*(NSW|QLD|VIC|SA|WA|TAS|NT|ACT)?\s*(\d{4})?$/i) || [];
  return {
    property_address: full,
    formatted_address: address.formatted_address || full,
    street_address: (address.street_address || first || "").trim(),
    suburb: (address.suburb || m[1] || "").trim(),
    state: (address.state || m[2] || "").trim().toUpperCase(),
    postcode: (address.postcode || m[3] || "").trim(),
    country: address.country || "AU",
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    google_place_id: address.google_place_id || "",
    address_verified: !!address.address_verified,
    address_source: address.address_source || "typed",
  };
}

export function groupSlotsByDate(slots) {
  const map = new Map();
  (slots || []).forEach((s) => {
    if (!map.has(s.date)) map.set(s.date, { date: s.date, date_display: s.date_display, times: [] });
    map.get(s.date).times.push(s);
  });
  return [...map.values()];
}
